// Entry point for the authenticated application (index.html).
import '../css/main.css';
import { html, render } from './utilities/html.js';
import { configProblems } from './config.js';
import { getSupabase } from './api/client.js';
import { bootstrapSession, redirectToLogin, signOut } from './auth/session.js';
import { startIdleTimeout } from './auth/idle-timeout.js';
import { createRouter } from './router.js';
import { appGuard } from './route-guards.js';
import { routes } from './modules/index.js';
import { mountStaffLayout } from './layouts/staff-layout.js';
import { getState } from './state.js';
import { brandMark } from './components/visuals.js';
import { pageSkeleton, emptyState, renderError, toast } from './components/feedback.js';
import { icon } from './utilities/icons.js';

const root = document.getElementById('app');

function bootScreen({ title, messages = [], action = '' }) {
  render(root, html`<div class="boot-screen"><div class="boot-card">
    ${brandMark(52)}<h1>${title}</h1>${messages.map((m) => html`<p>${m}</p>`)}${action}
  </div></div>`);
}

async function main() {
  const problems = configProblems();
  if (problems.length) {
    bootScreen({
      title: 'Setup required',
      messages: [
        'NGW EMS is not connected to its Supabase project yet, so sign-in is disabled. No demo or mock login is available.',
        ...problems,
        'Copy .env.example to .env.local, add the staging project URL and public anon key, then restart the dev server. See README → Local development.',
      ],
    });
    return;
  }

  let result;
  try {
    result = await bootstrapSession();
  } catch (error) {
    console.error(error);
    bootScreen({
      title: 'We couldn’t load your workspace',
      messages: [error?.message ?? 'Unexpected error.'],
      action: html`<button class="btn btn--primary" type="button" id="retry">${icon('refresh')}Try again</button>`,
    });
    document.getElementById('retry')?.addEventListener('click', () => window.location.reload());
    return;
  }

  if (result.status === 'signed_out') return redirectToLogin();
  if (result.status === 'mfa_challenge') return window.location.replace('/login.html?step=mfa&next=' + encodeURIComponent(window.location.pathname));
  if (result.status === 'no_membership') {
    bootScreen({
      title: 'No active access',
      messages: ['Your account is signed in but has no active membership in an organization. It may be pending, suspended or deactivated.', 'Contact your administrator if you think this is a mistake.'],
      action: html`<button class="btn btn--primary" type="button" id="leave">${icon('logout')}Sign out</button>`,
    });
    document.getElementById('leave')?.addEventListener('click', () => signOut());
    return;
  }

  let router;
  const layout = mountStaffLayout(root, { navigate: (p) => router.navigate(p) });
  router = createRouter({
    routes,
    outlet: layout.outlet,
    guard: appGuard,
    onRouteChange: layout.onRouteChange,
    renderLoading: (el) => render(el, pageSkeleton()),
    renderNotFound: (el) => render(el, html`<div class="card">${emptyState({ title: 'Page not found', message: 'The page you’re looking for doesn’t exist or has moved.', iconName: 'search', action: html`<a class="btn" href="/">Back to dashboard</a>` })}</div>`),
    renderForbidden: (el) => render(el, html`<div class="card">${emptyState({ title: 'You don’t have access to this page', message: 'Your role doesn’t include the permission this page needs. Ask an administrator if you need access.', iconName: 'lock', action: html`<a class="btn" href="/">Back to dashboard</a>` })}</div>`),
    renderError: (el, error, retry) => renderError(el, error, retry),
  });
  await router.start();

  // Idle timeout from organization settings.
  const minutes = getState().membership?.session_idle_minutes ?? 30;
  startIdleTimeout({
    minutes,
    onWarn: (seconds) => toast(`You’ll be signed out in about ${seconds} seconds due to inactivity.`, { type: 'info', timeout: 15000 }),
    onTimeout: () => signOut({ reason: 'idle' }),
  });

  // React to sign-out in another tab or token revocation.
  getSupabase().auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') redirectToLogin('signed_out');
  });
}

main();
